import { createEvent, createEffect, sample, restore } from 'effector'
import { devicesService } from 'features/devices.service'
import { IS_FIREFOX } from 'constants/system'
import { captureError } from 'utils/sentry'
// import { meetingModeSwitcherService } from 'features/meeting-mode-switcher.service'

class CallUserMicCaptureService {
  private audioContext: AudioContext | null = null
  private workletNode: AudioWorkletNode | null = null
  private audioSource: MediaStreamAudioSourceNode | null = null

  private readonly defaultSampleRate = 16384

  readonly startFx = createEffect<MediaStream | null, void>()
  readonly pauseFx = createEffect<void, void>()
  readonly stopFx = createEffect<void, void>()

  readonly setUserInputVolume = createEvent<number>()
  readonly $userInputVolume = restore(this.setUserInputVolume, 0)

  readonly gotAudioChunk = createEvent<Uint8Array>()

  constructor() {
    this.startFx.use(this.startImpl)
    this.pauseFx.use(this.pauseImpl)
    this.stopFx.use(this.stopImpl)

    sample({
      clock: devicesService.microphone.$stream,
      target: this.startFx
    })

    sample({
      clock: this.stopFx.doneData,
      fn: () => 0,
      target: this.$userInputVolume
    })

    // sample({
    //   clock: meetingModeSwitcherService.$currentMeetingMode,
    //   source: devicesService.microphone.$stream,
    //   target: this.startFx
    // })
  }

  private pauseImpl = (): void => {
    try {
      this.audioSource?.disconnect()
      this.audioSource = null

      if (window.isDevMode) {
        console.log('mic paused (context + worklet still alive)')
      }
    } catch (err) {
      captureError(err)
    }
  }

  private stopImpl = (): void => {
    try {
      this.audioSource?.disconnect()
      this.audioSource = null

      this.workletNode?.disconnect()
      this.workletNode = null

      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close().catch(captureError)
      }
      this.audioContext = null

      if (window.isDevMode) {
        console.log('mic capture fully stopped and cleaned')
      }
    } catch (err) {
      captureError(err)
    }
  }

  private startImpl = async (stream: MediaStream | null) => {
    this.pauseImpl()

    if (!stream) return

    if (!stream.getAudioTracks().length) {
      console.warn('no audio tracks available in stream')
      return
    }

    try {
      if (!this.audioContext) {
        this.audioContext = IS_FIREFOX
          ? new AudioContext({ latencyHint: 'interactive' })
          : new AudioContext({
              sampleRate: this.defaultSampleRate,
              latencyHint: 'interactive'
            })

        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume()
        }

        await this.audioContext.audioWorklet.addModule(
          '/worklets/MicProcessor.js'
        )

        this.workletNode = new AudioWorkletNode(
          this.audioContext,
          'mic-processor'
        )

        this.workletNode.port.onmessage = (event: MessageEvent) => {
          const { volume, chunk } = event.data

          if (typeof volume === 'number') {
            this.setUserInputVolume(volume)
          }
          if (chunk) {
            this.gotAudioChunk(new Uint8Array(chunk))
          }
        }

        this.workletNode.connect(this.audioContext.destination)
      }

      // переподключаем только источник
      this.audioSource?.disconnect()
      this.audioSource = this.audioContext.createMediaStreamSource(stream)
      this.audioSource.connect(this.workletNode!)

      if (window.isDevMode) {
        console.log(
          'mic capture (re)started, tracks:',
          stream.getAudioTracks().map((t) => `${t.label} (${t.readyState})`)
        )
      }
    } catch (err) {
      captureError(err)
    }
  }

  getCurrentSampleRate = (): number => {
    return this.audioSource?.context.sampleRate ?? this.defaultSampleRate
  }

  subscribe = (): VoidFunction => {
    return (): void => {
      this.stopImpl()
    }
  }
}

export const callUserMicCaptureService = new CallUserMicCaptureService()
